import { AlertTriangle } from "lucide-react";
import type { ReactElement } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LibraryImportReview } from "@/components/library/LibraryImportReview";
import { LibraryDuplicateWarnings } from "@/components/library/LibraryDuplicateWarnings";
import { libraryCopy, type LibraryDuplicateWarning } from "@/lib/library";
import type { LibraryCategory } from "@/lib/libraryCategories";
import type { EditableDraft, LibraryRecord, PendingLibrarySave } from "@/components/library/myLibraryHelpers";

type Props = {
  isMobile: boolean;
  // Import dialog
  importOpen: boolean;
  handleImportOpenChange: (open: boolean) => void;
  handleImportEscapeKeyDown: (e: KeyboardEvent) => void;
  items: LibraryRecord[];
  categories: LibraryCategory[];
  initialCategoryIds: string[];
  saving: boolean;
  importAnnouncementSetter: (msg: string) => void;
  handleImportFocusRestored: (target: "primary" | "fallback") => void;
  handleImportReviewRowFocusChange: (validity: "valid" | "invalid") => void;
  myLibraryReturnTo: string;
  onCreateCategory: (input: { name: string; color: string }) => Promise<LibraryCategory | null>;
  onImportSave: (drafts: EditableDraft[], categoryIds: string[]) => Promise<void>;
  onOpenExisting: (id: string) => void;
  // Editor dialog
  editorOpen: boolean;
  handleEditorOpenChange: (open: boolean) => void;
  renderEditorForm: (suffix: "mobile" | "desktop") => ReactElement;
  // Duplicate dialog
  duplicateDialogOpen: boolean;
  setDuplicateDialogOpen: (open: boolean) => void;
  duplicateWarnings: LibraryDuplicateWarning[];
  pendingSave: PendingLibrarySave | null;
  onConfirmSaveAnyway: (pending: PendingLibrarySave) => void;
};

/** Bundles My Library's three modals: Import, Editor (each as Sheet on
 *  mobile / Dialog on desktop), and the Duplicate-source warning Dialog. */
export const MyLibraryDialogs = (p: Props) => (
  <>
    {p.isMobile ? (
      <Sheet open={p.importOpen} onOpenChange={p.handleImportOpenChange}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto" onEscapeKeyDown={p.handleImportEscapeKeyDown}>
          <SheetHeader>
            <SheetTitle>Import sources</SheetTitle>
            <SheetDescription>Paste ISBNs, title/author lines, or CSV rows, then review the batch before saving it into My Library.</SheetDescription>
          </SheetHeader>
          <LibraryImportReview
            items={p.items}
            categories={p.categories}
            initialCategoryIds={p.initialCategoryIds}
            saving={p.saving}
            onCreateCategory={p.onCreateCategory}
            onSave={p.onImportSave}
            onOpenExisting={p.onOpenExisting}
            onAnnouncementChange={p.importAnnouncementSetter}
            onFocusRestored={p.handleImportFocusRestored}
            onReviewRowFocusChange={p.handleImportReviewRowFocusChange}
            myLibraryReturnTo={p.myLibraryReturnTo}
          />
        </SheetContent>
      </Sheet>
    ) : (
      <Dialog open={p.importOpen} onOpenChange={p.handleImportOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" onEscapeKeyDown={p.handleImportEscapeKeyDown}>
          <DialogHeader>
            <DialogTitle>Import sources</DialogTitle>
            <DialogDescription>Paste ISBNs, title/author lines, or CSV rows, then review the batch before saving it into My Library.</DialogDescription>
          </DialogHeader>
          <LibraryImportReview
            items={p.items}
            categories={p.categories}
            initialCategoryIds={p.initialCategoryIds}
            saving={p.saving}
            onCreateCategory={p.onCreateCategory}
            onSave={p.onImportSave}
            onOpenExisting={p.onOpenExisting}
            onAnnouncementChange={p.importAnnouncementSetter}
            onFocusRestored={p.handleImportFocusRestored}
            onReviewRowFocusChange={p.handleImportReviewRowFocusChange}
            myLibraryReturnTo={p.myLibraryReturnTo}
          />
        </DialogContent>
      </Dialog>
    )}

    {p.isMobile ? (
      <Sheet open={p.editorOpen} onOpenChange={p.handleEditorOpenChange}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{libraryCopy.editorTitle}</SheetTitle>
            <SheetDescription>Keep original-language titles and author names intact while refining the saved record for later citation work.</SheetDescription>
          </SheetHeader>
          {p.renderEditorForm("mobile")}
        </SheetContent>
      </Sheet>
    ) : (
      <Dialog open={p.editorOpen} onOpenChange={p.handleEditorOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{libraryCopy.editorTitle}</DialogTitle>
            <DialogDescription>Keep original-language titles and author names intact while refining the saved record for later citation work.</DialogDescription>
          </DialogHeader>
          {p.renderEditorForm("desktop")}
        </DialogContent>
      </Dialog>
    )}

    <Dialog open={p.duplicateDialogOpen} onOpenChange={p.setDuplicateDialogOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" /> Possible duplicate source
          </DialogTitle>
          <DialogDescription>We found one or more existing library records that look similar. You can keep editing or save anyway if this is a different edition, translation, or volume.</DialogDescription>
        </DialogHeader>
        <LibraryDuplicateWarnings
          warnings={p.duplicateWarnings}
          items={p.items}
          onOpenExisting={(itemId) => {
            p.setDuplicateDialogOpen(false);
            p.onOpenExisting(itemId);
          }}
        />
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => p.setDuplicateDialogOpen(false)}>Keep editing</Button>
          <Button onClick={() => p.pendingSave && p.onConfirmSaveAnyway(p.pendingSave)} disabled={p.saving}>Save anyway</Button>
        </div>
      </DialogContent>
    </Dialog>
  </>
);
